import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSupabase, handleSupabaseError } from "../utils";

// Types
export interface EmailSignature {
  id: string;
  connection_id: string;
  provider_config_key: string;
  name: string;
  content: string;
  owner_id: string;
  owner_email: string;
  owner_name: string;
  connection_email?: string;
  is_default: boolean;
  is_shared: boolean;
  created_at: string;
}

interface CreateSignatureRequest {
  connection_id: string;
  provider_config_key: string;
  name: string;
  content: string;
  is_default?: boolean;
}

interface UpdateSignatureRequest {
  id: string;
  name?: string;
  content?: string;
  is_default?: boolean;
}

// Direct Supabase functions using RLS
async function fetchUserSignatures(): Promise<EmailSignature[]> {
  const supabase = getSupabase();

  // Get current user
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error("User not authenticated");
  }

  // Use the helper function we created in the migration
  const { data, error } = await supabase.rpc("get_user_accessible_signatures", {
    user_id: user.id,
  });

  if (error) {
    handleSupabaseError(error, "fetch email signatures");
  }

  return data || [];
}

async function fetchConnectionSignatures(connectionId: string): Promise<EmailSignature[]> {
  const supabase = getSupabase();

  // Get current user
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error("User not authenticated");
  }

  // Use the helper function we created in the migration
  const { data, error } = await supabase.rpc("get_connection_signatures", {
    user_id: user.id,
    conn_id: connectionId,
  });

  if (error) {
    handleSupabaseError(error, "fetch connection signatures");
  }

  return data || [];
}

async function createSignature(request: CreateSignatureRequest): Promise<EmailSignature> {
  const supabase = getSupabase();

  // Get current user
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error("User not authenticated");
  }

  const { data, error } = await supabase
    .from("email_signatures")
    .insert({
      connection_id: request.connection_id,
      provider_config_key: request.provider_config_key,
      name: request.name,
      content: request.content,
      is_default: request.is_default || false,
      owner_id: user.id,
    })
    .select()
    .single();

  if (error) {
    handleSupabaseError(error, "create email signature");
  }

  // Return in the expected format (add computed fields)
  return {
    ...data,
    owner_email: user.email || "",
    owner_name: user.user_metadata?.full_name || user.email || "",
    is_shared: false,
  };
}

async function updateSignature(request: UpdateSignatureRequest): Promise<EmailSignature> {
  const supabase = getSupabase();

  const updateData: any = {};
  if (request.name !== undefined) updateData.name = request.name;
  if (request.content !== undefined) updateData.content = request.content;
  if (request.is_default !== undefined) updateData.is_default = request.is_default;

  const { data, error } = await supabase
    .from("email_signatures")
    .update(updateData)
    .eq("id", request.id)
    .select()
    .single();

  if (error) {
    handleSupabaseError(error, "update email signature");
  }

  // Get user info for computed fields
  const {
    data: { user },
  } = await supabase.auth.getUser();

  return {
    ...data,
    owner_email: user?.email || "",
    owner_name: user?.user_metadata?.full_name || user?.email || "",
    is_shared: false,
  };
}

async function deleteSignature(signatureId: string): Promise<void> {
  const supabase = getSupabase();

  const { error } = await supabase.from("email_signatures").delete().eq("id", signatureId);

  if (error) {
    handleSupabaseError(error, "delete email signature");
  }
}

// React Query hooks
export function useEmailSignatures(enabled: boolean = true) {
  return useQuery({
    queryKey: ["email-signatures"],
    queryFn: fetchUserSignatures,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useConnectionSignatures(connectionId: string | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ["connection-signatures", connectionId],
    queryFn: () => fetchConnectionSignatures(connectionId!),
    enabled: enabled && !!connectionId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSignature,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["email-signatures"] });
      void queryClient.invalidateQueries({ queryKey: ["connection-signatures", data.connection_id] });
    },
  });
}

export function useUpdateSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSignature,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["email-signatures"] });
      void queryClient.invalidateQueries({ queryKey: ["connection-signatures", data.connection_id] });
    },
  });
}

export function useDeleteSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSignature,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["email-signatures"] });
      void queryClient.invalidateQueries({ queryKey: ["connection-signatures"] });
    },
  });
}
